import type { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import React from 'react';
import { Box } from '@mui/material';
import type { IPropsWithTestId } from '../../../../constants/data-element-tracking-ids';
import Typography, {
  TypographyVariant,
} from '../../../../component_library/components/Typography';
import {
  ButtonVariant,
  LinkButton,
} from '../../../../component_library/components/Button';
import { useModuleWithBanner } from './hooks';
import { BREAKPOINT } from '../../../../component_library/utils/breakpoints';
import type { HtmlFromDotcms } from '../../../../dotcms/dotcms-models/converters-from-dotcms';
import HtmlBox from '../../../../component_library/components/HtmlBox';
import { useThemeToken } from '../../../../hooks/use-get-theme/use-get-theme';

interface IProps {
  headlineHtml?: HtmlFromDotcms;
  button:
  | (IPropsWithTestId & {
    buttonCtaText: string;
    buttonDestinationLink: string;
  })
  | undefined;
  renderButtonElement?: (
    props: IPropsWithTestId & {
      label: string | undefined;
      buttonVariant: ButtonVariant;
    },
  ) => ReactNode;
}

const ModuleWithBanner: React.FC<PropsWithChildren<IProps>> = ({
  headlineHtml,
  button,
  renderButtonElement,
  children,
}) => {
  const { theme, muiTheme } = useModuleWithBanner();
  const themeToken = useThemeToken();

  const rootStyles: CSSProperties = {
    backgroundColor: theme.colors.primary,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    columnGap: '64px',
    borderRadius: '10px',
    padding: '88px 64px',
    [muiTheme.breakpoints.down('tablet')]: {
      padding: '64px 32px',
    },
    [muiTheme.breakpoints.only('mobile')]: {
      flexDirection: 'column',
      rowGap: '40px',
      padding: '64px 24px',
      textAlign: 'center',
    },
  };

  const headerStyles = {
    marginBottom: '12px',

    [muiTheme.breakpoints.only('mobile')]: {
      marginBottom: '16px',
    },
  };

  return (
    <Box sx={rootStyles}>
      <Box sx={{ width: 'inherit' }}>
        {headlineHtml && (
          <Typography
            variant={{
              [BREAKPOINT.DESKTOP]: TypographyVariant.H2,
              [BREAKPOINT.TABLET_MINI]: TypographyVariant.H3,
            }}
            color={theme.colors.grayscale.white}
            additionalStyles={headerStyles}
            muiTheme={muiTheme}
            theme={theme}
          >
            <HtmlBox html={headlineHtml} />
          </Typography>
        )}
        <Typography
          variant={{
            [BREAKPOINT.DESKTOP]: TypographyVariant.LARGE_BODY_REGULAR,
            [BREAKPOINT.MOBILE]: TypographyVariant.MEDIUM_BODY_REGULAR,
          }}
          color={theme.colors.grayscale.white}
          muiTheme={muiTheme}
          theme={theme}
        >
          {children}
        </Typography>
      </Box>
      {renderButtonElement &&
        button &&
        renderButtonElement({
          label: button.buttonCtaText,
          dataElementId: button.dataElementId,
          buttonVariant: ButtonVariant.GHOST,
        })}
      {!renderButtonElement && button && (
        <LinkButton
          to={button.buttonDestinationLink}
          themeToken={themeToken}
          variant={ButtonVariant.GHOST}
          dataElementId={button.dataElementId}
          additionalStyles={{ flexShrink: '0' }}
        >
          {button.buttonCtaText}
        </LinkButton>
      )}
    </Box>
  );
};

export default ModuleWithBanner;
